import React, { useState, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { OrderTypes, parsedQueryAppConfigResponse } from '@amzn/awsscmui-argo-inventory-exception/common/main';
import { Box, Link } from '@amzn/awsui-components-react-v3';

import { isEmpty } from 'lodash';

import { httpFetch, EXCEPTION_URL_MAPPING } from '../../hooks/useHttp';
import useLocalStorage from '../../hooks/useLocalStorage';
import { Context } from '../../store';
import { AppContext, AppType } from '../../store/appContext';
import { URIParams, ReferenceQueryResponseItem, Model, ReceiveType, AsyncState } from '../../store/types';
import { SERIAL_ERROR_CODES } from '../ScanSerials/constants';
import { getOrderNumber, getOrderType, getOrderLineItemNumber, getOrderTypeForException } from '../Utils';
import { ArgoException } from './ArgoException';
import { BoostException } from './BoostException';
import {
  SERIAL_ERRORS,
  EXCEPTION_IGNORED_ERROR_CODES,
  JANUS_RESOLVER_GROUP,
  DEFAULT_EXCEPTION_STATE,
} from './constants';
import { NotifyProps } from './types';

export const WithExceptions = (WrapperComponent: any) => {
  const HocComponent = (props: any) => {
    const [visible, setVisible] = useState(false);
    const [notify, setNotify] = useState<null | NotifyProps>(null);
    const { referenceId, receiveType, modelId } = useParams<URIParams>();
    const { state } = useContext(Context);
    const appContext = useContext(AppContext);

    const [appConfig, setAppConfig] = useState({
      resolverGroupOptionDefinitions: new Map(),
      loadingResolverConfig: true,
    });
    const [preferredSite] = useLocalStorage('unifiedReceivePreferredSite');
    const inventoryAdjustment = receiveType === 'inventory_adjustment';

    const { serial, selectedModel, inputReference } = props;

    const references: ReferenceQueryResponseItem[] = state?.receiveReferences || [];
    const reference: ReferenceQueryResponseItem | undefined = references.find(
      (ref: ReferenceQueryResponseItem) => ref.receiveReference.receiveReferenceId === referenceId,
    );

    const mpnIpnId = serial?.mpnIpnId || modelId;

    const model: Model = state?.models?.find((model: Model) => model.mpnIpnId === mpnIpnId);
    const isPO = reference?.receiveReference.externalReferenceType === ReceiveType.AWS_PO;
    const isJanus = reference?.receiveReference?.externalReferenceType === ReceiveType.ASN_JANUS;

    const seletedOrderNumber = isPO ? reference?.receiveReference.externalReferenceValue : getOrderNumber({ model });
    const orderNumber = isEmpty(seletedOrderNumber) ? '' : seletedOrderNumber;
    const orderType = inventoryAdjustment ? getOrderType(model) : reference?.receiveReference?.externalReferenceType;
    const lineItemNumber = getOrderLineItemNumber({ model });

    const orderTypeForException = useMemo(() => {
      if (!!orderType) {
        return getOrderTypeForException(orderType);
      }
      return '';
    }, [orderType]);

    const preferredClusterName: string = preferredSite?.value?.slice(0, 3);
    const preferredSiteName: string = preferredSite?.value?.slice(3) || '';

    const modifiedPreferredSiteName =
      !isEmpty(preferredSite?.value) && preferredSiteName?.length > 0 && preferredSiteName?.length < 3
        ? preferredClusterName
            ?.concat(
              Array(3 - preferredSiteName?.length)
                .fill('0')
                .join(''),
            )
            ?.concat(preferredSiteName)
        : preferredSite?.value;

    const site = reference?.receiveReference?.siteName
      ? reference?.receiveReference?.siteName
      : modifiedPreferredSiteName;

    const shipmentID =
      reference?.receiveReference?.externalReferenceType !== ReceiveType.AWS_PO
        ? reference?.receiveReference?.externalReferenceValue
        : null;
    const trackingNUmber = reference?.receiveReference?.trackingId;
    const modelNumber = selectedModel?.modelNumber;
    const ipnNumber = model?.expectedIPN;

    const exceptionOrderType = {
      label: orderTypeForException ? orderTypeForException : '',
      value: orderTypeForException
        ? Object.keys(OrderTypes)[Object.values(OrderTypes).indexOf(orderTypeForException)]
        : '',
    };
    const holdingContainer = `${site}_Physical_Sideline_Location`;

    const getFormState = () => {
      switch (props?.code) {
        case 'po_not_found':
          return {
            type: 'NO_SHIPMENTID_INVALID_PO_NOT_RMA',
            values: {
              ORDER_NUMBER: orderNumber,
              TITLE: `No shipment id AND invalid po AND not rma`,
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              // @ts-ignore
              ORDER_TYPE: exceptionOrderType,
              SERIAL_NUMBERS: [],
              SHIPMENT_ID: inputReference,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
        case 'shipment_id_not_found':
        case 'shipment_not_found':
          return {
            type: 'INVALID_SHIPMENTID_NO_PO_NOT_RMA',
            values: {
              ORDER_NUMBER: orderNumber,
              SHIPMENT_ID: shipmentID || inputReference,
              TITLE: `Invalid shipment id AND no po AND not rma`,
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              SERIAL_NUMBERS: [],
              ORDER_TYPE: exceptionOrderType,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
        case 'model_not_available_to_scan':
          return {
            type: 'MODEL_UNAVAILABLE_TO_SCAN_OR_TYPE',
            values: {
              SHIPMENT_ID: shipmentID,
              ORDER_NUMBER: orderNumber,
              TITLE: `Model ID unavailable to scan or type`,
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              APN_IPN_NUMBER: ipnNumber,
              SERIAL_NUMBERS: [],
              ENTIRE_ORDER: true,
              ORDER_TYPE: exceptionOrderType,
              MODEL: modelNumber,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
        case 'model_not_exists_catalogue':
          return {
            type: isJanus ? 'UNEXPECTED_MODEL_JANUS_ASN' : 'INVALID_MODEL_NOT_IN_ITEM_CATALOGUE',
            values: {
              SHIPMENT_ID: shipmentID,
              ORDER_NUMBER: orderNumber,
              TITLE: isJanus ? 'Unexpected model id' : `Invalid model that does not exist in catalogue`,
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              APN_IPN_NUMBER: ipnNumber,
              SERIAL_NUMBERS: [],
              ENTIRE_ORDER: true,
              ORDER_TYPE: exceptionOrderType,
              MODEL: modelNumber,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
        case SERIAL_ERROR_CODES.SERIALID_NOT_EXPECTED_IN_SHIPMENT_PSIR_NOT_REQUIRED:
        case SERIAL_ERROR_CODES.SERIALID_NOT_EXPECTED_IN_SHIPMENT_PSIR_REQUIRED:
        case SERIAL_ERROR_CODES.SERIALID_NOT_EXPECTED_IN_SHIPMENT:
        case SERIAL_ERROR_CODES.serial_id_not_exists_asn:
          return {
            type: 'UNEXPECTED_SERIAL_JANUS_ASN',
            values: {
              SHIPMENT_ID: shipmentID,
              TITLE: 'Unexpected serial id',
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              ORDER_NUMBER: orderNumber,
              APN_IPN_NUMBER: ipnNumber,
              SERIAL_NUMBERS: [{ label: serial?.serialNumber }],
              ORDER_LINE_ITEM_NUMBER: lineItemNumber,
              ORDER_TYPE: exceptionOrderType,
              MODEL: model?.expectedMPN,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
        case SERIAL_ERROR_CODES.COMPOSITE_DUPLICATE_SERIAL:
          return {
            type: 'COMPOSITE_DUPLICATE_SERIAL',
            values: {
              SHIPMENT_ID: shipmentID,
              TITLE: 'Composite duplicate serial',
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              ORDER_NUMBER: orderNumber,
              APN_IPN_NUMBER: ipnNumber,
              SERIAL_NUMBERS: [{ label: serial?.serialNumber }],
              ORDER_LINE_ITEM_NUMBER: lineItemNumber,
              ORDER_TYPE: exceptionOrderType,
              MODEL: model?.expectedMPN,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
        case 'serail_not_available_to_scan':
          return {
            type: 'SERIAL_UNAVAILABLE_TO_SCAN_OR_TYPE',
            values: {
              SHIPMENT_ID: shipmentID,
              TITLE: 'Serial id unavailable to scan or type',
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              ORDER_NUMBER: orderNumber,
              APN_IPN_NUMBER: ipnNumber,
              SERIAL_NUMBERS: [],
              ORDER_LINE_ITEM_NUMBER: lineItemNumber,
              ORDER_TYPE: exceptionOrderType,
              MODEL: model?.expectedMPN,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
        case 'serial_qty_not_available_to_scan':
          return {
            type: 'SERIAL_QTY_UNAVAILABLE_TO_SCAN_OR_TYPE',
            values: {
              SHIPMENT_ID: shipmentID,
              TITLE: 'Total quantity is not available, create exception.',
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              ORDER_NUMBER: orderNumber,
              APN_IPN_NUMBER: ipnNumber,
              SERIAL_NUMBERS: [],
              ORDER_LINE_ITEM_NUMBER: lineItemNumber,
              ORDER_TYPE: exceptionOrderType,
              MODEL: model?.expectedMPN,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
        case 'shipment_po_rma_not_available_to_scan':
          return {
            type: 'NO_SHIPMENTID_NO_PO_NOT_RMA',
            values: {
              SHIPMENT_ID: shipmentID,
              TITLE: 'No shipment id AND no po AND not rma',
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              ORDER_NUMBER: orderNumber,
              APN_IPN_NUMBER: ipnNumber,
              SERIAL_NUMBERS: [],
              ORDER_LINE_ITEM_NUMBER: lineItemNumber,
              ORDER_TYPE: exceptionOrderType,
              MODEL: model?.expectedMPN,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
        default:
          return {
            type: '',
            values: {
              ...DEFAULT_EXCEPTION_STATE,
              SHIPMENT_ID: shipmentID,
              TITLE: '',
              SITE: site,
              TRACKING_NUMBER: trackingNUmber,
              ORDER_NUMBER: orderNumber,
              APN_IPN_NUMBER: ipnNumber,
              SERIAL_NUMBERS: [],
              ORDER_LINE_ITEM_NUMBER: lineItemNumber,
              ORDER_TYPE: exceptionOrderType,
              MODEL: model?.expectedMPN,
              HOLDING_CONTAINER: holdingContainer,
            },
          };
      }
    };
    const formState = getFormState();
    const exceptionState = !isJanus
      ? formState
      : { ...formState, values: { ...formState.values, ASSIGNED_TO: JANUS_RESOLVER_GROUP } };

    const skipExceptionLink =
      (SERIAL_ERRORS.includes(props?.code) && serial?.state !== AsyncState.ERROR) ||
      EXCEPTION_IGNORED_ERROR_CODES.includes(props?.code);

    const getAppConfig = async () => {
      try {
        const response = await httpFetch(EXCEPTION_URL_MAPPING.appConfig, {
          headers: {
            'content-type': 'application/json; charset=utf-8',
          },
          body: {},
          method: 'POST',
        });
        setAppConfig(
          parsedQueryAppConfigResponse({
            loadingResolverConfig: false,
            queryError: null,
            appConfigQueryResult: {
              getReceiveExceptionResolverConfig: response?.exceptionTypeToResolverGroupConfigList,
            },
            shouldIncludeAllExceptionTypes: false,
          }),
        );
      } catch (error) {
        setAppConfig(
          parsedQueryAppConfigResponse({
            loadingResolverConfig: false,
            queryError: null,
            appConfigQueryResult: {},
            shouldIncludeAllExceptionTypes: false,
          }),
        );
      }
    };

    const onClickCreateException = () => {
      if (appContext.appType === AppType.BOOST) {
        getAppConfig();
      }
      setVisible(true);
      setNotify(null);
    };

    const ExceptionComponentSelector = appContext.appType === AppType.BOOST ? BoostException : ArgoException;

    return (
      <>
        <WrapperComponent {...props} />
        {!skipExceptionLink && (
          <Box color="text-body-secondary" variant="span" margin={{ right: 'xxs' }}>
            <Link onFollow={onClickCreateException} target="_blank">
              <Box color="text-status-info" fontSize="body-s" variant="span">
                create exception
              </Box>
            </Link>
            <Box color="text-body-secondary" fontSize="body-s" variant="span">
              {' '}
              and sideline the part.
            </Box>
          </Box>
        )}
        {visible && (
          <ExceptionComponentSelector
            setNotify={setNotify}
            notify={notify}
            formState={exceptionState}
            appConfig={appConfig}
            onDismiss={() => setVisible(false)}
          />
        )}
      </>
    );
  };
  HocComponent.displayName = `WithExceptions(${WrapperComponent.displayName || WrapperComponent.name || 'Component'})`;

  return HocComponent;
};
